'use client'
import { LineCheck } from '@qr-front/icons/line-check'
import { LineChevronDownSmall } from '@qr-front/icons/line-chevron-down-small'
import { LineClose } from '@qr-front/icons/line-close'
import { inputCls } from '@qr-front/ui/components/Input'
import {
	Select,
	SelectContent,
	SelectGroup,
	SelectIcon,
	SelectItem,
	SelectSeparator,
	SelectTrigger,
	SelectValue,
} from '@qr-front/ui/components/Select'
import { cn } from '@qr-front/ui/utils'
import { useTranslations } from 'next-intl'

import {
	CUSTOM_LABEL_MAX_LENGTH,
	TContactType,
	TContactTypeOption,
} from './contactEntryTypes'

const itemCls =
	'type-body-medium h-10 p-2 text-dark-grey focus:text-dark-grey data-[state=checked]:bg-purple-8 data-[state=checked]:text-dark-grey hover:bg-generator-bg hover:text-dark-grey'

type TContactTypeSelectProps = {
	ariaLabel: string
	options: TContactTypeOption[]
	value: TContactType
	label?: string
	fieldId: string
	onChange: (type: TContactType) => void
	onLabelChange: (label: string) => void
}

export const ContactTypeSelect = ({
	ariaLabel,
	options,
	value,
	label = '',
	fieldId,
	onChange,
	onLabelChange,
}: TContactTypeSelectProps) => {
	const t = useTranslations()

	const selectedOption = options.find((option) => option.type === value)

	if (value === 'custom') {
		return (
			<div className="relative">
				<input
					id={fieldId}
					autoFocus
					type="text"
					value={label}
					maxLength={CUSTOM_LABEL_MAX_LENGTH}
					onChange={(event) =>
						onLabelChange(event.currentTarget.value)
					}
					className={cn(inputCls(), 'pr-20')}
					aria-label={`${ariaLabel} ${t(
						'generator__content_form_section__about__contact_section__type__custom__label',
					)}`}
				/>
				<span className="type-body-caption pointer-events-none absolute right-10 top-1/2 -translate-y-1/2 text-grey">
					{label.length}/{CUSTOM_LABEL_MAX_LENGTH}
				</span>
				<button
					type="button"
					onClick={() => {
						onLabelChange('')
						onChange(options[0]!.type)
					}}
					aria-label={t(
						'generator__content_form_section__about__contact_section__type__custom__back',
					)}
					className="absolute right-3 top-1/2 -translate-y-1/2 text-grey hover:text-black"
				>
					<LineClose width={16} height={16} />
				</button>
			</div>
		)
	}

	return (
		<Select
			value={value}
			onValueChange={(type) => onChange(type as TContactType)}
		>
			<SelectTrigger
				id={fieldId}
				aria-label={ariaLabel}
				className={cn(
					inputCls(),
					'group/select relative flex w-full items-center gap-2 pr-10 text-left text-dark-grey data-[state=open]:text-dark-grey hover:text-dark-grey',
				)}
			>
				{selectedOption ? (
					<selectedOption.Icon
						width={20}
						height={20}
						className="shrink-0"
					/>
				) : null}
				<SelectValue>
					{selectedOption ? t(selectedOption.labelKey) : null}
				</SelectValue>
				<SelectIcon className="absolute right-3 top-1/2 -translate-y-1/2">
					<LineChevronDownSmall
						width={16}
						height={16}
						className="text-dark-grey transition-transform duration-300 group-data-[state=closed]/select:rotate-0 group-data-[state=open]/select:rotate-180"
					/>
				</SelectIcon>
			</SelectTrigger>

			<SelectContent
				sideOffset={-4}
				position="popper"
				className="z-10 w-[var(--radix-select-trigger-width)]"
			>
				<SelectGroup className="mt-1 w-full rounded-8 border border-solid border-border-grey bg-white p-2">
					{options.map(({ type, Icon, labelKey }, index) => (
						<div key={type}>
							{type === 'custom' && index > 0 ? (
								<SelectSeparator className="mx-0 bg-border-grey" />
							) : null}
							<SelectItem
								value={type}
								className={itemCls}
								leadingIcon={<Icon width={20} height={20} />}
								icon={<LineCheck width={16} height={16} />}
							>
								{t(labelKey)}
							</SelectItem>
						</div>
					))}
				</SelectGroup>
			</SelectContent>
		</Select>
	)
}
